import { useParams, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Video, videoCategories } from "@shared/schema";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Youtube, ArrowLeft, Eye, Clock, Calendar, Share2, ThumbsUp } from "lucide-react";
import { useEffect, useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { PageContainer } from "@/components/layout/PageContainer";

export default function YouTubeDetail() {
  const params = useParams<{ id: string }>();
  const videoId = params?.id ?? "";

  const { data: video, isLoading } = useQuery<Video>({
    queryKey: ["/api/videos", videoId],
    enabled: !!videoId,
  });

  // Track view count - only once per page load
  const [viewTracked, setViewTracked] = useState(false);

  const viewMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/videos/${videoId}/view`, {}),
    onSuccess: () => {
      setViewTracked(true);
    },
    onError: (error) => {
      // Silently fail - don't show error to user for view tracking
      console.error('[YouTubeDetail] View tracking error:', error);
    },
  });

  // Track view on mount - only once
  useEffect(() => {
    if (videoId && !viewTracked && !viewMutation.isPending && !viewMutation.isSuccess) {
      viewMutation.mutate();
    }
  }, [videoId]); // Only run when videoId changes

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">로딩 중...</p>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Youtube className="h-24 w-24 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">영상을 찾을 수 없습니다</h2>
          <p className="text-muted-foreground mb-6">요청하신 영상이 존재하지 않거나 삭제되었습니다.</p>
          <Link href="/youtube">
            <Button>
              <ArrowLeft className="h-4 w-4 mr-2" />
              목록으로 돌아가기
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const categoryInfo = videoCategories.find(c => c.id === video.category);

  const formatViews = (views: number | null) => {
    if (!views) return "0";
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views.toString();
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getYouTubeEmbedUrl = (youtubeId: string) => {
    return `https://www.youtube.com/embed/${youtubeId}?autoplay=0&rel=0`;
  };

  return (
    <PageContainer size="default">
        {/* Back Button */}
        <Link href="/youtube">
          <Button variant="ghost" className="mb-6" data-testid="button-back">
            <ArrowLeft className="h-4 w-4 mr-2" />
            목록으로 돌아가기
          </Button>
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Video Section */}
          <div className="lg:col-span-2">
            <Card className="overflow-hidden">
              {/* YouTube Player */}
              <div className="aspect-video bg-black">
                <iframe
                  src={getYouTubeEmbedUrl(video.youtubeId)}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="youtube-player"
                />
              </div>

              {/* Video Info */}
              <CardHeader>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Badge variant="secondary">{categoryInfo?.label}</Badge>
                  {video.region !== "ALL" && (
                    <Badge variant="outline">{video.region}</Badge>
                  )}
                  {video.isFeatured && (
                    <Badge className="bg-primary">추천</Badge>
                  )}
                </div>
                <CardTitle className="text-2xl">{video.titleKo || video.title}</CardTitle>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Stats */}
                <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground pb-6 border-b">
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    <span>{formatViews(video.views)} 조회</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>{formatDuration(video.duration)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{new Date(video.publishedAt).toLocaleDateString('ko-KR')}</span>
                  </div>
                  {video.likes !== null && video.likes > 0 && (
                    <div className="flex items-center gap-2">
                      <ThumbsUp className="h-4 w-4" />
                      <span>{formatViews(video.likes)}</span>
                    </div>
                  )}
                </div>

                {/* Description */}
                {(video.descriptionKo || video.description) && (
                  <div>
                    <h3 className="font-semibold mb-2">설명</h3>
                    <p className="text-muted-foreground whitespace-pre-wrap">
                      {video.descriptionKo || video.description}
                    </p>
                  </div>
                )}

                {/* Tags */}
                {video.tags && video.tags.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">태그</h3>
                    <div className="flex flex-wrap gap-2">
                      {video.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          #{tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="default"
                    className="flex-1"
                    onClick={() => window.open(video.youtubeUrl, '_blank')}
                    data-testid="button-watch-youtube"
                  >
                    <Youtube className="h-4 w-4 mr-2" />
                    YouTube에서 보기
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: video.titleKo || video.title,
                          text: video.descriptionKo || video.description || '',
                          url: window.location.href,
                        });
                      } else {
                        navigator.clipboard.writeText(window.location.href);
                      }
                    }}
                    data-testid="button-share"
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">채널 정보</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Youtube className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">OZK News</p>
                    <p className="text-sm text-muted-foreground">한인 뉴스 채널</p>
                  </div>
                </div>
                <Button className="w-full" size="lg" data-testid="button-subscribe-sidebar">
                  <Youtube className="h-4 w-4 mr-2" />
                  구독하기
                </Button>
                <p className="text-sm text-muted-foreground">
                  호주 한인 커뮤니티를 위한 뉴스, 시황 분석, 인터뷰 등 다양한 콘텐츠를 제공합니다.
                </p>
              </CardContent>
            </Card>

            {/* Video Category Info */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg">카테고리</CardTitle>
              </CardHeader>
              <CardContent>
                <Link href={`/youtube?category=${video.category}`}>
                  <Button variant="outline" className="w-full justify-start">
                    {categoryInfo?.label}의 다른 영상 보기
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageContainer>
  );
}
